import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Marquee from "react-fast-marquee";

const tickerItems = [
  "Dream Entertainment",
  "Luxury Weddings",
  "Sangeet Nights",
  "Baby Showers",
  "Milestone Birthdays",
  "Anniversaries",
  "Backyard Parties",
];

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/gallery", label: "Gallery" },
  { to: "/packages", label: "Packages" },
  { to: "/testimonials", label: "Testimonials" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <div
        data-testid="top-ticker"
        className="fixed top-0 left-0 right-0 z-50 h-10 bg-black/80 backdrop-blur-xl border-b border-primary/15 flex items-center overflow-hidden"
      >
        <Marquee speed={30} gradient={false}>
          {tickerItems.map((item) => (
            <span
              key={item}
              className="mx-8 text-[10px] tracking-[0.35em] uppercase text-primary/80 whitespace-nowrap"
            >
              {item} <span className="text-accent/70 mx-2">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      <nav
        data-testid="main-nav"
        className={`fixed top-10 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "glass border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="leading-none">
            <img
              src="/images/logo.png"
              alt="Dream Entertainment"
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-4 xl:gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-xs xl:text-sm tracking-widest uppercase whitespace-nowrap transition-colors duration-300 hover:text-primary ${
                    isActive ? "text-primary" : "text-foreground/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/booking"
              data-testid="nav-book-now-btn"
              className="rounded-full bg-primary text-primary-foreground px-5 xl:px-6 py-2.5 text-xs xl:text-sm font-medium tracking-wide whitespace-nowrap transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,0,127,0.35)]"
            >
              Book Now
            </Link>
          </div>

          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="md:hidden text-foreground p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-background/95 backdrop-blur-2xl flex flex-col items-center justify-center gap-8"
          >
            {links.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-serif text-4xl sm:text-5xl text-foreground hover:text-primary transition-colors duration-300"
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
            >
              <Link
                to="/booking"
                data-testid="nav-mobile-book-btn"
                className="rounded-full bg-primary text-primary-foreground px-10 py-4 text-lg font-medium"
              >
                Book Now
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
